import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import NotificationTemplateServ from '@api/NotificationTemplates/NotificationTemplateServ';
import {
    NOTIFICATION_TEMPLATES,
    NotificationTemplateDef,
    NotificationTemplateGroup,
    NotificationTemplateOverrides,
    fillTemplate,
    resolveTemplateTexts
} from '@api/NotificationTemplates/NotificationTemplates';

/**
 * AVISOS INTERNOS — los textos de las notificaciones, editables sin deploy.
 *
 * Pedido de Irving (2026-08-31): administrar TODAS las notificaciones como ya
 * se administran las plantillas de WhatsApp. Diferencia clave con aquéllas:
 * estos textos son nuestros — se editan y aplican al instante (con el caché de
 * 60 s de los emisores), sin pasar por la aprobación de Meta.
 *
 * Lo que se ve es el texto EFECTIVO (override o el original del catálogo);
 * "Restaurar" borra el override y el original vuelve solo. Las variables van
 * como `{nombre}` y se listan en el editor — una variable con typo no rompe el
 * aviso: se queda visible tal cual.
 */

// Tabs por grupo (pedido de Irving, 2026-08-31): con las 15 plantillas en una
// sola columna había que scrollear demasiado para llegar a Wallet.
const GRUPOS: { key: NotificationTemplateGroup; label: string; nota: string }[] = [
    { key: 'cliente', label: 'Cliente', nota: 'La campana y el push de la app del cliente, por estado del pedido.' },
    { key: 'staff', label: 'Staff', nota: 'La campana interna del admin.' },
    {
        key: 'wallet',
        label: 'Wallet',
        nota: 'El mensaje del pase de Google Wallet. La notificación de CERCANÍA a sucursal no es editable: Google solo asoma el pase, no acepta texto propio. La cuota del pase es de 3 avisos cada 24 h.'
    }
];

const VISTA_PREVIA_VARS: Record<string, string | number> = {
    puntos: 25,
    cliente: 'Ana López',
    total: '350.00',
    folio: '001310826-00042',
    minutos: 15,
    desde: '30/08/2026',
    usuario: ' (Karla)',
    insumo: 'Pan brioche',
    saldo: '-3.50',
    movimiento: '25 puntos',
    quedan: 'quedan'
};

export const InternalTemplates: React.FunctionComponent = () => {
    const [overrides, setOverrides] = useState<NotificationTemplateOverrides>({});
    const [editing, setEditing] = useState<NotificationTemplateDef | undefined>(undefined);
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');

    const cargar = async () => {
        setOverrides(await NotificationTemplateServ.getOverrides());
    };

    useEffect(() => {
        void cargar();
    }, []);

    const abrirEditor = (def: NotificationTemplateDef) => {
        const actual = overrides[def.key];
        setTitle(actual?.title || def.title);
        setBody(actual?.body || def.body);
        setEditing(def);
    };

    const guardar = async () => {
        if (!editing) return;
        showLoader();
        try {
            // Solo se guarda lo que DIFIERE del original: si el texto quedó
            // igual, no hay override que mantener.
            const override: { title?: string; body?: string } = {};
            if (title.trim() && title.trim() !== editing.title) override.title = title.trim();
            if (body.trim() && body.trim() !== editing.body) override.body = body.trim();
            if (override.title || override.body) {
                await NotificationTemplateServ.saveOverride(editing.key, override);
                showAlertMessage('Texto guardado — aplica en el próximo aviso (máx. 1 min)');
            } else {
                await NotificationTemplateServ.restoreDefault(editing.key);
                showAlertMessage('Texto igual al original: se quitó la personalización');
            }
            await cargar();
            setEditing(undefined);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const restaurar = async () => {
        if (!editing) return;
        showLoader();
        try {
            await NotificationTemplateServ.restoreDefault(editing.key);
            showAlertMessage('Texto original restaurado');
            await cargar();
            setEditing(undefined);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const personalizadas = useMemo(
        () => new Set(Object.keys(overrides).filter((key) => overrides[key])),
        [overrides]
    );

    const [grupoActivo, setGrupoActivo] = useState<NotificationTemplateGroup>('cliente');
    const grupo = GRUPOS.find((g) => g.key === grupoActivo) || GRUPOS[0];

    return (
        <Box p={2} sx={{ overflowY: 'auto', height: '100%' }}>
            <Stack spacing={2} maxWidth={900}>
                <Tabs value={grupoActivo} onChange={(_, value) => setGrupoActivo(value)}>
                    {GRUPOS.map((g) => (
                        <Tab
                            key={g.key}
                            value={g.key}
                            label={`${g.label} (${NOTIFICATION_TEMPLATES.filter((t) => t.group === g.key).length})`}
                        />
                    ))}
                </Tabs>
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                        {grupo.nota}
                    </Typography>
                    <Stack spacing={1}>
                        {NOTIFICATION_TEMPLATES.filter((t) => t.group === grupo.key).map((def) => {
                                const efectivo = resolveTemplateTexts(def.key, {}, overrides);
                                return (
                                    <Box
                                        key={def.key}
                                        onClick={() => abrirEditor(def)}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            p: 1.5,
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: 'action.hover' }
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                            <Typography variant="body2" fontWeight={700}>
                                                {def.label}
                                            </Typography>
                                            {personalizadas.has(def.key) && (
                                                <Chip size="small" color="info" label="Personalizado" />
                                            )}
                                            <Box flex={1} />
                                            <EditIcon fontSize="small" color="action" />
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {def.description}
                                        </Typography>
                                        <Typography variant="body2" mt={0.5}>
                                            <strong>{efectivo.title}</strong> — {efectivo.body}
                                        </Typography>
                                    </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </Stack>

            <Dialog open={!!editing} onClose={() => setEditing(undefined)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing?.label}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={0.5}>
                        <Typography variant="body2" color="text.secondary">
                            {editing?.description}
                        </Typography>
                        {!!editing?.vars.length && (
                            <Alert severity="info" sx={{ py: 0.5 }}>
                                Variables disponibles:{' '}
                                {editing.vars.map((variable) => `{${variable}}`).join(' · ')}
                            </Alert>
                        )}
                        <TextField
                            label="Título"
                            value={title}
                            onChange={(event) => setTitle(event.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Texto"
                            value={body}
                            onChange={(event) => setBody(event.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                Así se vería:
                            </Typography>
                            <Typography variant="body2">
                                <strong>{fillTemplate(title, VISTA_PREVIA_VARS)}</strong>
                                <br />
                                {fillTemplate(body, VISTA_PREVIA_VARS)}
                            </Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    {editing && personalizadas.has(editing.key) && (
                        <Button onClick={restaurar} color="inherit" startIcon={<RestartAltIcon />}>
                            Restaurar original
                        </Button>
                    )}
                    <Box flex={1} />
                    <Button onClick={() => setEditing(undefined)} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={guardar} variant="contained">
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InternalTemplates;
